// @ts-nocheck
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Divider, message, Spin } from "antd";
import cn from "classnames";
import { Accordion, ErrorField, FeatureToggle, RadioField, TitleField, TitlePage } from "~/components";
import {
  FormField,
  Wizard,
  UploadFile,
  AcceptPolitics
} from "~/pages/questionnaires/shared";
import { AttachmentsDetailsError, ClientId, NamesOfFieldEnum, Offer, OrgTypeEnum } from "~/typings/types";
import { configTitleFieldsOfSelfEmployed } from "../configTitleFields";
import { RulesFiles } from "../../../../shared/rules-files";

import "~/pages/questionnaires/shared/styles.css";
import styles from "~/pages/questionnaires/shared/Shared.module.scss";
import stylesStep from "./CommonSteps.module.scss";
import { useInitialTrigger, useScrollAndFocusField } from "~/hooks";
import { usePassportCheck } from "../../../../shared/passport-check/usePassportCheck";
import { PassportCheckComponent } from "../../../../shared/passport-check";
import { FieldOffer } from "../../../../shared/field-offer";
import { useAnalyticsContext } from "~/services/analytics";
import { usePersistentAccordion } from "../../../../hooks/usePersistentAccordion";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 МБ
const ALLOWED_PASSPORT_TYPES = ['.jpg', '.jpeg', '.png', '.pdf', 'image/jpeg', 'image/png', 'application/pdf'];

interface Props {
  isLoadingMount: boolean;
  isLoadingLogo: boolean;
  isLoadingPassport: boolean;
  logoFiles: File[];
  setLogoFileId: Dispatch<SetStateAction<number[]>>;
  setLogoFiles: Dispatch<File[]>;
  passportFiles: File[];
  setPassportFileId: Dispatch<SetStateAction<number[]>>;
  setPassportFiles: Dispatch<File[]>;
  logoError: AttachmentsDetailsError;
  passportError: AttachmentsDetailsError;
  setLogoError: Dispatch<AttachmentsDetailsError>;
  setPassportError: Dispatch<AttachmentsDetailsError>;
  COUNT_UPLOAD_LOGO_FILE?: number;
  COUNT_UPLOAD_PASSPORT_FILE?: number;
  setIsFirstLoadingFile: Dispatch<boolean>;
  orgType?: OrgTypeEnum;
  isLoadingDocumentAgreement1File: boolean;
  documentAgreement1Files: File[];
  setDocumentAgreement1FileId: Dispatch<SetStateAction<number[]>>;
  setDocumentAgreement1Files: Dispatch<File[]>;
  documentAgreement1Error: AttachmentsDetailsError;
  setDocumentAgreement1Error: Dispatch<AttachmentsDetailsError>;
  isLoadingDocumentAgreement2File: boolean;
  documentAgreement2Files: File[];
  setDocumentAgreement2FileId: Dispatch<SetStateAction<number[]>>;
  setDocumentAgreement2Files: Dispatch<File[]>;
  documentAgreement2Error: AttachmentsDetailsError;
  setDocumentAgreement2Error: Dispatch<AttachmentsDetailsError>;
  textLinkAttachment: string;
  setTextLinkAttachment: Dispatch<SetStateAction<string>>;
  isLoadingDocumentPricesTariffsFile: boolean;
  documentPricesTariffsFiles: File[];
  setDocumentPricesTariffsFileId: Dispatch<SetStateAction<number[]>>;
  setDocumentPricesTariffsFiles: Dispatch<File[]>;
  documentPricesTariffsError: AttachmentsDetailsError;
  setDocumentPricesTariffsError: Dispatch<AttachmentsDetailsError>;
  pricesTariffsLinkError: AttachmentsDetailsError;
  setPricesTariffsLinkError: Dispatch<AttachmentsDetailsError>;
  clientId: ClientId;
  isUploadOffer: boolean;
  setIsUploadOffer: Dispatch<SetStateAction<boolean>>;
  errorUploadOffer: AttachmentsDetailsError;
  setErrorUploadOffer: Dispatch<AttachmentsDetailsError>;
  offer?: Offer;
  textLinkOffer: string;
  setTextLinkOffer: Dispatch<SetStateAction<string>>;
  isGeneratedOffer: boolean;
  setIsGeneratedOffer: Dispatch<SetStateAction<boolean>>;
}

export const AttachmentsDetails = ({
  isLoadingMount,
  isLoadingPassport,
  passportFiles,
  setPassportFileId,
  setPassportFiles,
  passportError,
  setPassportError,
  COUNT_UPLOAD_PASSPORT_FILE,
  setIsFirstLoadingFile,
  orgType,
  isLoadingDocumentAgreement1File,
  documentAgreement1Files,
  setDocumentAgreement1FileId,
  setDocumentAgreement1Files,
  documentAgreement1Error,
  setDocumentAgreement1Error,
  isLoadingDocumentPricesTariffsFile,
  documentPricesTariffsFiles,
  setDocumentPricesTariffsFileId,
  setDocumentPricesTariffsFiles,
  documentPricesTariffsError,
  setDocumentPricesTariffsError,
  passportMainFiles,
  setPassportMainFiles,
  passportRegistrationFiles,
  setPassportRegistrationFiles,
  passportMainFilesError,
  setPassportMainFilesError,
  passportRegistrationFilesError,
  setPassportRegistrationFilesError,
  setPassportMainFileId,
  isLoadingPassportMainFileId,
  setPassportRegistrationFileId,
  isLoadingPassportRegistrationFile,
  textLinkAttachment,
  setTextLinkAttachment,
  pricesTariffsLinkError,
  setPricesTariffsLinkError,
  clientId,
  passportMainFileId,
  passportRegistrationFileId,
  isUploadOffer,
  setIsUploadOffer,
  errorUploadOffer,
  setErrorUploadOffer,
  offer,
  textLinkOffer,
  setTextLinkOffer,
  isGeneratedOffer,
  setIsGeneratedOffer,
}: Props) => {
  useScrollAndFocusField(NamesOfFieldEnum.PassportFileId);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-se-init_step6');

  useEffect(() => {
    if (analytics?.trackStepWebvisor && ((passportMainFiles && passportMainFiles.length > 0) || (passportRegistrationFiles && passportRegistrationFiles.length > 0))) {
      analytics?.trackStepWebvisor('webvisor_anketa_customer_step5');
    }
  }, [passportMainFiles, passportRegistrationFiles, analytics]);

  const passportMainFileIdSelected = passportMainFileId && passportMainFileId?.[0];
  const passportRegistrationFileIdSelected = passportRegistrationFileId && passportRegistrationFileId?.[0];

  const { status: statusMain, setStatus: setStatusMain, messages: messagesMain } = usePassportCheck({
    fileId: passportMainFileIdSelected,
    pageType: 'main',
    questionnaireUid: clientId,
    questionnaireType: 'ru_customer',
    shouldFetch: Boolean(passportMainFileIdSelected),
  });

  const { status: statusRegistration, setStatus: setStatusRegistration, messages: messagesRegistration } = usePassportCheck({
    fileId: passportRegistrationFileIdSelected,
    pageType: 'registration',
    questionnaireUid: clientId,
    questionnaireType: 'ru_customer',
    shouldFetch: Boolean(passportRegistrationFileIdSelected),
  });

  const openByStatusMain = useInitialTrigger(statusMain && statusMain !== "idle");
  const openByStatusRegistration = useInitialTrigger(statusRegistration && statusRegistration !== "idle");

  const {
    isOpen: openOfferAccordion,
    setIsOpen: setOpenOfferAccordion,
    open: openOffer,
  } = usePersistentAccordion("offerAccordionOpened", true);

  useEffect(() => {
    if (errorUploadOffer?.has) {
      openOffer();
    }
  }, [errorUploadOffer, openOffer]);

  return (
    <Wizard.Page>
      <TitlePage name="Фуух. Осталось совсем чуть-чуть" number="6" />
      <Divider style={{ margin: 0 }} />
      <div className={cn(stylesStep.containerAttachmentStep)}>
        {isLoadingMount ? (
          <div className={styles.containerLoader}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: "#3d935e" }} spin />} />
          </div>
        ) : (
          <>
            <Accordion
              title={(
                <TitleField
                  name={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PassportFileId]}
                  required
                />
              )}
              containerClassName={stylesStep.accordionContainerAttachmentStep}
              defaultOpen={true}
            >
              <div className={stylesStep.containerItem}>
                <div className={stylesStep.itemHead}>
                  <p style={{ fontSize: '12px', color: '#D32F2F', marginBottom: 4 }}>ВАЖНО:</p>
                  <ul className={stylesStep.listRequired}>
                    <li>Добавьте скан или фото 2-й и 3-й страниц паспорта, а также страницу с действующей пропиской.</li>
                    <li>Проверьте, чтобы данные были быть чётко видны (без засветов или закрытых элементов), особенно серия и номер паспорта.</li>
                  </ul>
                </div>
                <div className={stylesStep.itemContent} style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1 1 50%' }}>
                    <FormField
                      titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.PassportFileId]}
                      name={NamesOfFieldEnum.PassportMainFileId}
                      required
                      renderError={passportMainFilesError}
                    >
                      <div>
                        <UploadFile
                          id={NamesOfFieldEnum.PassportMainFileId}
                          uploadedFiles={passportMainFiles}
                          setUploadedFiles={setPassportMainFiles}
                          isLoadingProps={isLoadingPassportMainFileId}
                          setFileid={setPassportMainFileId}
                          count={1}
                          error={passportMainFilesError}
                          setError={setPassportMainFilesError}
                          setIsFirstLoadingFile={setIsFirstLoadingFile}
                          contentText="Загрузить главный разворот"
                          uploadButtonHeight={52}
                          mode="dropdown"
                          isSecondaryLoading={isLoadingPassportRegistrationFile && !passportMainFiles?.length}
                          allowedFileTypes={ALLOWED_PASSPORT_TYPES}
                          maxFileSize={MAX_FILE_SIZE}
                          required
                        />
                      </div>
                      {/* <PassportCheckComponent status={statusMain} messages={messagesMain} /> */}
                    </FormField>
                  </div>
                  <div style={{ flex: '1 1 50%' }}>
                    <FormField
                      titleField={""}
                      name={NamesOfFieldEnum.PassportRegistrationFileId}
                      required
                      renderError={passportRegistrationFilesError}
                    >
                      <div>
                        <UploadFile
                          id={NamesOfFieldEnum.PassportRegistrationFileId}
                          uploadedFiles={passportRegistrationFiles}
                          setUploadedFiles={setPassportRegistrationFiles}
                          isLoadingProps={isLoadingPassportRegistrationFile}
                          setFileid={setPassportRegistrationFileId}
                          count={1}
                          error={passportRegistrationFilesError}
                          setError={setPassportRegistrationFilesError}
                          setIsFirstLoadingFile={setIsFirstLoadingFile}
                          contentText="Загрузить страницу с пропиской"
                          mode="dropdown"
                          uploadButtonHeight={52}
                          isSecondaryLoading={isLoadingPassportMainFileId && !passportRegistrationFiles?.length}
                          allowedFileTypes={ALLOWED_PASSPORT_TYPES}
                          maxFileSize={MAX_FILE_SIZE}
                          required
                        />
                      </div>
                      {/* <PassportCheckComponent status={statusRegistration} messages={messagesRegistration} /> */}
                    </FormField>
                  </div>
                </div>
              </div>
            </Accordion>

            <Accordion
              isOpenExternal={openOfferAccordion}
              onToggle={setOpenOfferAccordion}
              title={(
                <TitleField
                  name={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.Offer]}
                  required
                />
              )}>
              <FieldOffer
                clientId={clientId}
                isUploadOffer={isUploadOffer}
                setIsUploadOffer={setIsUploadOffer}
                errorUploadOffer={errorUploadOffer}
                setErrorUploadOffer={setErrorUploadOffer}
                typeOffer="default"
                offer={offer}
                textLinkOffer={textLinkOffer}
                setTextLinkOffer={setTextLinkOffer}
                setIsGeneratedOffer={setIsGeneratedOffer}
                isGeneratedOffer={isGeneratedOffer}
                required
              />
            </Accordion>
          </>
        )}
      </div>
      <Divider style={{ margin: 0 }} />
      <AcceptPolitics />
    </Wizard.Page >
  );
};
